import { createContext, getOwner, useContext } from "solid-js";
import { reconcile, SetStoreFunction } from "solid-js/store";
import { z } from "zod";

import useValidatedParams from "@repo/ui/signals/useValidatedParams";
import assert from "@repo/utils/assert";
import UArray from "@repo/utils/UArray";
import UBoolean from "@repo/utils/UBoolean";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import Cart from "~/components/checkout/Cart";

import { Bookable, useBookables } from "./bookables";
import { Booking, useBookings } from "./bookings";
import { useCustomers } from "./customers";
import { Membership, useMemberships } from "./memberships";
import { Organization } from "./organizations";
import { PaymentMethod } from "./paymentMethods";
import { Promotion, usePromotions } from "./promotions";

export namespace BookingCheckout {
  export const classCost = {
    membership: {},
    credits: { credits: 1 },
    guestPasses: { guestPasses: 1 },
  } satisfies Record<string, Cart.Entry.Cost>;
}

const initialState = {
  guests: [] as Booking.Guest[],
  paymentMethodId: undefined as PaymentMethod.Id | undefined,
  useCreditsIfAvailable: true,
  useGuestPassesIfAvailable: true,
  promoCodeId: undefined as Promotion.Code.Id | undefined,
};

export const { Context, Provider, useBookingCheckout } = FeatureStore.init(
  "BookingCheckout",
  initialState,
  ([store, setStore], { withLogger }) => {
    const [customers, setCustomers] = useCustomers();
    const [, setBookings] = useBookings();
    const [bookables] = useBookables();
    const [memberships, setMemberships] = useMemberships();
    const [promotions, setPromotions] = usePromotions();

    const owner = getOwner()!;

    const params = useValidatedParams(
      z.object({
        orgId: Organization.Id.zodSchema(),
      }),
    );

    const context = useContext(CheckoutContext);
    assert(
      context,
      "CheckoutContext is required when calling useBookingCheckout.",
    );

    const customer = () => customers.forOrganization.own(params()?.orgId);

    const newPlan = () => memberships.planById(context.planId);

    const accessors = {
      newPlan: () => newPlan(),
      guests: () => store.guests,
      paymentMethodId: () => store.paymentMethodId,
      promoCode: () => promotions.codes.byId(store.promoCodeId),
      useCreditsIfAvailable: () => store.useCreditsIfAvailable,
      useGuestPassesIfAvailable: () => store.useGuestPassesIfAvailable,
      primaryPaymentOption: Object.assign(
        () => {
          const preferred = accessors.primaryPaymentOption.preferred();
          if (preferred === "credits")
            return accessors.useCreditsIfAvailable() ? "credits" : undefined;
          return preferred;
        },
        {
          preferred() {
            const unlimitedMembershipId =
              customer()?.classVoucherWallet.unlimitedMembershipId;
            if (unlimitedMembershipId) {
              return memberships.byId(unlimitedMembershipId)!.planId;
            }

            const membershipPlan = newPlan();
            if (
              membershipPlan &&
              // Don't apply membership if it doesn't include class credit
              membershipPlan.classLimit !== 0
            ) {
              if (membershipPlan.classLimit) return "credits";
              return context.planId;
            }

            if (customer()?.classVoucherWallet.count) return "credits";
          },
        },
      ),
      // Credit balance after plan purchase, but before class purchase
      peakCreditBalance: () =>
        (customer()?.classVoucherWallet.count || 0) +
        (newPlan()?.classLimit || 0),
      peakGuestPassBalance: () =>
        (customer()?.guestPassWallet.count || 0) +
        (newPlan()?.guestPassLimit || 0),
      cart: () => {
        const plan = newPlan();

        return Cart.applyPromoCode(
          [
            plan && {
              itemId: plan.id,
              quantity: 1,
            },
            {
              itemId: context.bookableId,
              quantity: 1,
              cost: (() => {
                const paymentOption = accessors.primaryPaymentOption();
                if (paymentOption === "credits")
                  return BookingCheckout.classCost.credits;
                if (Membership.Plan.Id.is(paymentOption))
                  return BookingCheckout.classCost.membership;
              })(),
            },
            store.guests.length > 0 && {
              itemId: context.bookableId,
              quantity: store.guests.length,
              label: "Guest:",
              metadata: {
                guest: true,
              },
              cost: (() => {
                const guestPasses = accessors.useGuestPassesIfAvailable()
                  ? Math.min(
                      store.guests.length,
                      accessors.peakGuestPassBalance(),
                    )
                  : 0;
                return {
                  guestPasses,
                  cents:
                    (store.guests.length - guestPasses) *
                    bookables.byId(context.bookableId)!.priceCents,
                };
              })(),
            },
          ].filter(UBoolean.isTruthy),
          accessors.promoCode(),
          owner,
        );
      },
      total: () => Cart.total(accessors.cart(), owner),
      unusedPromoCode: () => {
        if (!store.promoCodeId) return false;
        if (
          accessors
            .cart()
            .some((entry) => entry.promoCodeId === store.promoCodeId)
        )
          return false;
        return true;
      },
    };

    const mutations = {
      guests: Object.assign(
        ((...args: any) =>
          // @ts-expect-error
          setStore("guests", ...args)) as SetStoreFunction<typeof store.guests>,
        {
          add(
            guest:
              | Booking.Guest
              // Allow empty guest for drafts
              | {} = {},
          ) {
            setStore("guests", store.guests.length, guest);
          },
          remove(index: number) {
            setStore("guests", (guests) =>
              guests.filter((_, i) => i !== index),
            );
          },
        },
      ),
      paymentMethodId(paymentMethodId: PaymentMethod.Id) {
        setStore("paymentMethodId", paymentMethodId);
      },
      useCreditsIfAvailable(useCreditsIfAvailable: boolean) {
        setStore("useCreditsIfAvailable", useCreditsIfAvailable);
      },
      useGuestPassesIfAvailable(useGuestPassesIfAvailable: boolean) {
        setStore("useGuestPassesIfAvailable", useGuestPassesIfAvailable);
      },
      clearPromoCode() {
        setStore("promoCodeId", undefined);
      },
    };

    const actions = withLogger("action", {
      ...mutations,
      applyPromoCode(orgId: Organization.Id, code: string) {
        return setPromotions.forOrganization
          .checkPromoCode(orgId, code)
          .then((promoCode) => {
            setStore("promoCodeId", promoCode.id);
          });
      },
      async submit() {
        if (accessors.total().cents > 0 && !store.paymentMethodId)
          throw new Error("Payment method required.");

        const cart = accessors.cart();
        const total = accessors.total();

        const [planEntries, bookableEntries] = UArray.partition(
          cart,
          (entry): entry is Cart.Entry & { itemId: Membership.Plan.Id } =>
            Membership.Plan.Id.is(entry.itemId),
        );

        // TODO: Move to batch checkout endpoint to support multiple plan purchases
        if (planEntries.length > 1)
          throw new Error("Multiple plan purchase not supported.");

        const membershipRes = UArray.hasLengthOf(planEntries, 1)
          ? await setMemberships.create(
              planEntries[0].itemId,
              store.paymentMethodId!,
              planEntries[0].promoCodeId,
            )
          : undefined;

        if (!customer())
          await setCustomers.forOrganization.fetchOwn(params()?.orgId!);

        const [bookableEntry, ...guestEntries] = bookableEntries;
        assert(bookableEntry, "Bookable is expected.");

        return setBookings
          .create([
            {
              bookableId: context.bookableId,
              resourceId: (() => {
                const paymentOption = accessors.primaryPaymentOption();

                if (paymentOption === "credits")
                  return customer()!.classVoucherWallet.id;
                if (Membership.Plan.Id.is(paymentOption))
                  return customer()!.classVoucherWallet.unlimitedMembershipId!;

                return store.paymentMethodId!;
              })(),
              promoCodeId: bookableEntry.promoCodeId,
            },
            ...store.guests.map((guest, i) => ({
              bookableId: context.bookableId,
              resourceId:
                total.guestPasses > i
                  ? customer()!.guestPassWallet.id
                  : store.paymentMethodId!,
              guestDetails: guest,
              // Applies promo code to first entry that is not assigned a guest
              // pass resourceId
              promoCodeId:
                i === total.guestPasses + 1
                  ? guestEntries[0]?.promoCodeId
                  : undefined,
            })),
          ])
          .then((res) => {
            setStore(reconcile(initialState));
            return {
              ...res,
              membership: membershipRes?.membership,
            };
          })
          .catch((e) => {
            throw Object.assign(e, {
              purchasedPlanId: planEntries[0]?.itemId,
            });
          });
      },
    });

    return { accessors, actions };
  },
);

export const CheckoutContext = createContext<{
  bookableId: Bookable.Id;
  planId: Membership.Plan.Id;
}>();
